// ---------- ad consent (UMP) ----------
// Google's User Messaging Platform, which is the only thing allowed to ask a player in the EEA or
// the UK whether ads may use their data. We never draw that form ourselves: it is rendered from
// the message configured in the AdMob console, and a hand-rolled banner would be neither certified
// nor accepted.
//
// WHY THIS IS A MODULE OF ITS OWN, and not four lines inside ads.ts: the consent decides two
// separate things at two separate moments — whether a banner may be requested at all (startup),
// and whether the settings dialog must offer a permanent way back to that choice (any time after).
// The second one is a Google requirement, not a nicety: once the form has been shown, an entry
// point to it has to exist for as long as the app does.
//
// ON THE WEB THERE IS NOTHING TO ASK, and that is a finding rather than an omission. The web build
// serves no ads at all (see the header of ads.ts: a site uses AdSense, and AdSense inside an app
// WebView breaks the program rules, so the two surfaces stay apart), so there is no ad consent to
// collect. What the PWA does store — the progress and the anonymous token — exists to deliver the
// feature the player asked for, which is the one case that needs no consent prompt. It needs a
// privacy policy, not a dialog.
//
// The web branch is therefore an early return, and it has to be: the plugin's own web
// implementation is a STUB THAT LIES. It answers `status: REQUIRED`,
// `privacyOptionsRequirementStatus: REQUIRED`, `canRequestAds: true` unconditionally, and its
// showConsentForm() is a console.log. Trusting it would put a row in the settings dialog whose
// button does nothing at all — on the one platform where nobody has any ads to consent to.

import { AdMob, AdmobConsentStatus, type AdmobConsentInfo } from '@capacitor-community/admob';

import { isNative } from './platform';

/**
 * The privacy-options requirement, compared as a STRING and not through the plugin's enum, because
 * that enum cannot be imported: its barrel (`dist/esm/consent/index`) re-exports the four other
 * consent files and forgets `privacy-options-requirement-status.enum`, so the name is missing from
 * the package root at runtime as well as in the types. Reaching into `dist/` would tie us to the
 * plugin's build layout for one word.
 *
 * The string IS the contract rather than a guess: the Android side sends
 * `getPrivacyOptionsRequirementStatus().name()` (AdConsentExecutor.java), i.e. the UMP enum's own
 * name, and `REQUIRED` is the only value that asks anything of us — `NOT_REQUIRED` and `UNKNOWN`
 * both mean "no entry point to offer".
 */
const PRIVACY_OPTIONS_REQUIRED = 'REQUIRED';

export interface ConsentGate {
  /** An ad may be requested. False also covers "we could not ask", which is deliberate: a sheet
   *  with no banner is a lost cent, a banner served without a consent we failed to obtain is a
   *  policy breach. */
  ads: boolean;
  /** Google requires a permanent entry point back to the choice — the settings row. */
  privacyOptions: boolean;
}

const NONE: ConsentGate = { ads: false, privacyOptions: false };

let gate: ConsentGate = NONE;

/** Vrai dès qu'un aller-retour UMP a CONCLU — quelle que soit sa réponse. Faux tant qu'il a
 *  échoué (pas de réseau au lancement, formulaire quitté par le bouton retour), et c'est cette
 *  distinction que `ensureConsent()` exploite : un refus est définitif et se respecte, une
 *  question qu'on n'a pas pu poser se repose. */
let gathered = false;

/** What the last gathering concluded. Read by the settings dialog, which paints on open and must
 *  not restart a UMP round trip to decide whether to show one row. */
export function consentGate(): ConsentGate {
  return gate;
}

function readInfo(info: AdmobConsentInfo): ConsentGate {
  return {
    ads: !!info.canRequestAds,
    privacyOptions: String(info.privacyOptionsRequirementStatus) === PRIVACY_OPTIONS_REQUIRED,
  };
}

/**
 * Asks, once, at startup — and BEFORE `AdMob.initialize()`, which is why this can be awaited from
 * ads.ts without a chicken-and-egg problem: the plugin's consent side talks to
 * `UserMessagingPlatform` directly (see AdConsentExecutor.java) and has no dependency on the ads
 * SDK being up. The order matters the other way round though — initialising the ads SDK first
 * would be requesting ads before the answer is in.
 *
 * `showConsentForm()` maps to `loadAndShowConsentFormIfRequired`, so it is already a no-op when
 * nothing is required; the status is checked anyway to save a round trip on the launches — every
 * launch after the first one — where the answer is settled.
 *
 * `tagForUnderAgeOfConsent` stays at its default of false: the store listing declares a 13+
 * audience (cf. PLAY.md). Flip it here, and only here, if that declaration ever changes.
 *
 * To see the form outside the EEA, pass `debugGeography: AdmobConsentDebugGeography.EEA` with the
 * device's `testDeviceIdentifiers` — never committed, since it would fake a jurisdiction for
 * everybody.
 */
export async function gatherConsent(): Promise<ConsentGate> {
  if (!isNative()) return (gate = NONE);
  try {
    const info = await AdMob.requestConsentInfo();
    gathered = true;
    if (info.status === AdmobConsentStatus.REQUIRED && info.isConsentFormAvailable) {
      return (gate = readInfo(await AdMob.showConsentForm()));
    }
    return (gate = readInfo(info));
  } catch {
    gathered = false;
    // No network on first launch, a missing message configuration, a form the player dismissed by
    // backing out: none of it may take the game down, and none of it earns the right to serve an
    // ad. The sheet simply gets the banner's height back — same fallback as a failed load.
    return (gate = NONE);
  }
}

/**
 * Reopens the choice from the settings dialog. Returns false when nothing was shown, so the caller
 * can leave its row alone rather than pretend something happened.
 *
 * The gate is refreshed from the form's own answer: a player who has just withdrawn consent must
 * stop seeing the row's effects immediately, and one who granted it does not need a relaunch. The
 * banner already on screen is left as it is — tearing it down mid-session to re-request it would
 * shift the sheet under the player's hands for a change they can only have made deliberately.
 */
export async function openPrivacyOptions(): Promise<boolean> {
  if (!isNative() || !gate.privacyOptions) return false;
  try {
    await AdMob.showPrivacyOptionsForm();
    const info = await AdMob.requestConsentInfo();
    gathered = true;
    gate = readInfo(info);
    return true;
  } catch {
    return false;
  }
}

/**
 * Le verrou, en reposant la question SEULEMENT si le premier tour n'a jamais abouti.
 *
 * POURQUOI : `gatherConsent()` ne tourne qu'une fois, au démarrage (src/main.ts). Un lancement
 * hors réseau, ou un formulaire UMP quitté par le bouton retour, laissait donc le verrou fermé
 * pour TOUTE la session — pas de bannière, et surtout pas de pub récompensée, donc plus aucun
 * pack déblocable jusqu'au prochain lancement, alors même que le réseau était revenu depuis.
 * C'est le symptôme « la bannière ne s'affiche pas tout le temps ».
 *
 * Un refus, lui, n'est jamais rejoué : `gathered` est vrai dès qu'UMP a répondu, et on rend le
 * verrou tel quel. Reposer la question à un joueur qui a dit non serait du harcèlement de
 * formulaire, et c'est exactement ce que les règles de Google interdisent.
 */
export async function ensureConsent(): Promise<ConsentGate> {
  if (!isNative()) return NONE;
  if (gathered) return gate;
  return gatherConsent();
}
